import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  apiErrorMessage,
  deleteAdminFollow,
  getAdminFriendshipSummary,
  listAdminFollowRequests,
  listAdminFollows,
  normalizeResponseData,
  updateAdminFollowRequestStatus
} from "../../core/api/adminApi";
import { useAuth } from "../../core/auth/AuthContext";
import type {
  AdminFollowRequestItem,
  AdminFollowRequestsListPayload,
  AdminFollowItem,
  AdminFollowsListPayload,
  AdminFriendshipSummaryPayload
} from "../../shared/types/admin";

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function RequestStatusBadge({ value }: { value?: string }) {
  const normalized = (value || "").toUpperCase();
  const ok = normalized === "ACCEPTED";
  return <span className={`badge ${ok ? "ok" : ""}`}>{normalized || "-"}</span>;
}

function UserRef({
  label,
  userId,
  name,
  email,
  onOpen
}: {
  label: string;
  userId?: string;
  name?: string;
  email?: string;
  onOpen: (userId?: string) => void;
}) {
  return (
    <div>
      <div className="muted">{label}</div>
      <button className="link-button" type="button" onClick={() => onOpen(userId)} disabled={!userId}>
        {name || "(ohne Name)"}
      </button>
      <div className="muted">{email || userId || "-"}</div>
    </div>
  );
}

export function FriendshipsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialUserFilter = searchParams.get("userId") || "";

  const [query, setQuery] = useState("");
  const [userIdFilter, setUserIdFilter] = useState(initialUserFilter);
  const [requestStatus, setRequestStatus] = useState("PENDING");
  const [size, setSize] = useState(20);

  const [followsPage, setFollowsPage] = useState(0);
  const [requestsPage, setRequestsPage] = useState(0);

  const [followsPayload, setFollowsPayload] = useState<AdminFollowsListPayload>();
  const [requestsPayload, setRequestsPayload] = useState<AdminFollowRequestsListPayload>();
  const [summary, setSummary] = useState<AdminFriendshipSummaryPayload>();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [status, setStatus] = useState("Bereit.");
  const [error, setError] = useState("");
  const [requestTargets, setRequestTargets] = useState<Record<string, string>>({});

  const openUser = (userId?: string) => {
    if (!userId) return;
    navigate(`/users/${encodeURIComponent(userId)}`);
  };

  const loadData = async (nextFollowsPage = followsPage, nextRequestsPage = requestsPage, nextSize = size) => {
    setLoading(true);
    setError("");
    setStatus("Lade Freundschaften...");
    try {
      const params = {
        userId: userIdFilter.trim() || undefined,
        query: query.trim() || undefined,
        page: nextFollowsPage,
        size: nextSize
      };
      const [followsResponse, requestsResponse] = await Promise.all([
        listAdminFollows(token, params),
        listAdminFollowRequests(token, {
          ...params,
          page: nextRequestsPage,
          status: requestStatus
        })
      ]);
      setFollowsPayload(normalizeResponseData(followsResponse));
      const requestData = normalizeResponseData(requestsResponse);
      setRequestsPayload(requestData);

      if (userIdFilter.trim()) {
        try {
          const summaryResponse = await getAdminFriendshipSummary(token, userIdFilter.trim());
          setSummary(normalizeResponseData(summaryResponse));
        } catch {
          setSummary(undefined);
        }
      } else {
        setSummary(undefined);
      }

      const defaults: Record<string, string> = {};
      (requestData?.items || []).forEach((item) => {
        if (item.id) {
          defaults[item.id] = (item.status || "PENDING").toUpperCase();
        }
      });
      setRequestTargets(defaults);
      setStatus("Freundschaften geladen.");
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData(0, 0, size);
  }, []);

  const removeFollow = async (item: AdminFollowItem) => {
    if (!item.id) return;
    setActionLoading(true);
    setError("");
    setStatus("Follow wird entfernt...");
    try {
      await deleteAdminFollow(token, item.id);
      setStatus("Follow entfernt.");
      await loadData(followsPage, requestsPage, size);
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Follow entfernen fehlgeschlagen.");
    } finally {
      setActionLoading(false);
    }
  };

  const applyRequestStatus = async (item: AdminFollowRequestItem) => {
    if (!item.id) return;
    const target = (requestTargets[item.id] || item.status || "PENDING").toUpperCase() as
      | "PENDING"
      | "ACCEPTED"
      | "REJECTED"
      | "WITHDRAWN";
    setActionLoading(true);
    setError("");
    setStatus(`Request-Status -> ${target}...`);
    try {
      await updateAdminFollowRequestStatus(token, item.id, target);
      setStatus(`Request ${item.id} auf ${target} gesetzt.`);
      await loadData(followsPage, requestsPage, size);
    } catch (e) {
      setError(apiErrorMessage(e));
      setStatus("Request-Update fehlgeschlagen.");
    } finally {
      setActionLoading(false);
    }
  };

  const followsTotalPages = Math.max(1, followsPayload?.totalPages ?? 1);
  const requestsTotalPages = Math.max(1, requestsPayload?.totalPages ?? 1);

  return (
    <section>
      <div className="view-headline">
        <h1>Friendships</h1>
        <p className="muted">Follower/Following und Follow-Requests prüfen, nachvollziehen und bearbeiten.</p>
      </div>

      <div className="card">
        <div className="friendships-toolbar">
          <div>
            <label>Suche</label>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, E-Mail, City, UID"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setFollowsPage(0);
                  setRequestsPage(0);
                  void loadData(0, 0, size);
                }
              }}
            />
          </div>
          <div>
            <label>User ID (optional)</label>
            <input
              value={userIdFilter}
              onChange={(event) => setUserIdFilter(event.target.value)}
              placeholder="Firebase UID"
            />
          </div>
          <div>
            <label>Requests Status</label>
            <select value={requestStatus} onChange={(event) => setRequestStatus(event.target.value)}>
              <option value="ALL">ALL</option>
              <option value="PENDING">PENDING</option>
              <option value="ACCEPTED">ACCEPTED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="WITHDRAWN">WITHDRAWN</option>
            </select>
          </div>
          <div>
            <label>Size</label>
            <select value={size} onChange={(event) => setSize(Number(event.target.value))}>
              {[10, 20, 50, 100].map((entry) => (
                <option key={entry} value={entry}>
                  {entry}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="primary"
            onClick={() => {
              setFollowsPage(0);
              setRequestsPage(0);
              void loadData(0, 0, size);
            }}
            disabled={loading}
          >
            {loading ? "Lade..." : "Laden"}
          </button>
        </div>

        {summary ? (
          <div className="friendships-summary-grid">
            <div className="kpi-card">
              <div className="kpi-label">User</div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {summary.user?.name || summary.user?.email || summary.userId || "-"}
              </div>
              <div className="kpi-sub">{summary.user?.userId || "-"}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Followers</div>
              <div className="kpi-value">{summary.followersCount ?? 0}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Following</div>
              <div className="kpi-value">{summary.followingCount ?? 0}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Pending Incoming</div>
              <div className="kpi-value">{summary.pendingIncomingCount ?? 0}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Pending Outgoing</div>
              <div className="kpi-value">{summary.pendingOutgoingCount ?? 0}</div>
            </div>
          </div>
        ) : null}

        <div className="muted" style={{ marginTop: 8 }}>{status}</div>
        {error ? <div className="error-box">{error}</div> : null}

        <div className="friendships-grid">
          <div className="card">
            <div className="section-header">
              <label>Follows</label>
              <div className="pager">
                <button
                  type="button"
                  disabled={followsPage <= 0}
                  onClick={async () => {
                    const next = Math.max(0, followsPage - 1);
                    setFollowsPage(next);
                    await loadData(next, requestsPage, size);
                  }}
                >
                  ‹
                </button>
                <span className="pager-info">
                  Seite {followsPage + 1}/{followsTotalPages} · {followsPayload?.total ?? 0}
                </span>
                <button
                  type="button"
                  disabled={followsPage >= followsTotalPages - 1}
                  onClick={async () => {
                    const next = followsPage + 1;
                    setFollowsPage(next);
                    await loadData(next, requestsPage, size);
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="item-list friendships-list">
              {(followsPayload?.items || []).length === 0 ? (
                <div className="muted">Keine Follows gefunden.</div>
              ) : null}
              {(followsPayload?.items || []).map((item) => (
                <div key={item.id} className="item-row">
                  <div className="friendship-row-top">
                    <strong>{formatDate(item.createdDate)}</strong>
                    <button
                      type="button"
                      className="danger"
                      disabled={actionLoading}
                      onClick={() => void removeFollow(item)}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="friendship-pair">
                    <UserRef
                      label="Follower"
                      userId={item.follower?.userId}
                      name={item.follower?.name}
                      email={item.follower?.email}
                      onOpen={openUser}
                    />
                    <div className="friendship-arrow">→</div>
                    <UserRef
                      label="Followed"
                      userId={item.followed?.userId}
                      name={item.followed?.name}
                      email={item.followed?.email}
                      onOpen={openUser}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="section-header">
              <label>Follow Requests</label>
              <div className="pager">
                <button
                  type="button"
                  disabled={requestsPage <= 0}
                  onClick={async () => {
                    const next = Math.max(0, requestsPage - 1);
                    setRequestsPage(next);
                    await loadData(followsPage, next, size);
                  }}
                >
                  ‹
                </button>
                <span className="pager-info">
                  Seite {requestsPage + 1}/{requestsTotalPages} · {requestsPayload?.total ?? 0}
                </span>
                <button
                  type="button"
                  disabled={requestsPage >= requestsTotalPages - 1}
                  onClick={async () => {
                    const next = requestsPage + 1;
                    setRequestsPage(next);
                    await loadData(followsPage, next, size);
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="item-list friendships-list">
              {(requestsPayload?.items || []).length === 0 ? (
                <div className="muted">Keine Follow-Requests gefunden.</div>
              ) : null}
              {(requestsPayload?.items || []).map((item) => (
                <div key={item.id} className="item-row">
                  <div className="friendship-row-top">
                    <strong>{formatDate(item.requestedOn || item.createdDate)}</strong>
                    <RequestStatusBadge value={item.status} />
                  </div>
                  <div className="friendship-pair">
                    <UserRef
                      label="Requester"
                      userId={item.requester?.userId}
                      name={item.requester?.name}
                      email={item.requester?.email}
                      onOpen={openUser}
                    />
                    <div className="friendship-arrow">→</div>
                    <UserRef
                      label="Requested"
                      userId={item.requested?.userId}
                      name={item.requested?.name}
                      email={item.requested?.email}
                      onOpen={openUser}
                    />
                  </div>
                  <div className="friendship-request-actions">
                    <select
                      value={requestTargets[item.id || ""] || (item.status || "PENDING")}
                      onChange={(event) =>
                        setRequestTargets((prev) => ({ ...prev, [item.id || ""]: event.target.value }))
                      }
                      disabled={!item.id}
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="ACCEPTED">ACCEPTED</option>
                      <option value="REJECTED">REJECTED</option>
                      <option value="WITHDRAWN">WITHDRAWN</option>
                    </select>
                    <button
                      type="button"
                      className="primary"
                      disabled={actionLoading || !item.id}
                      onClick={() => void applyRequestStatus(item)}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
